<template>
    <div data-spec-class="grid-wired">
        <div class="flex items-end justify-between py-6">
            <div>
                <slot name="actions" />
            </div>
            <div
                v-if="searchableAttributeLabels"
                class="flex items-end"
            >
                <BaseFieldInput
                    :model-value="search"
                    :is-clearable="true"
                    :placeholder="searchableAttributeLabels"
                    :error-messages="validationError?.search"
                    name="search"
                    class="!w-64 py-0.5 focus:!w-80"
                    data-spec-class="grid-wired__search"
                    @update:model-value="(value) => emits('update:search', value)"
                    @clear="onSearchClear"
                    @keyup.enter="updatePage(1)"
                >
                    <template #addOn>
                        <div class="bg-white pl-1 dark:bg-gray-800">
                            <IconMagnifyingGlass
                                class="h-4 w-4 cursor-pointer fill-blue-800 dark:fill-blue-700"
                                data-spec-class="grid-wired__search_submit"
                                @click="updatePage(1)"
                            />
                        </div>
                    </template>
                </BaseFieldInput>
            </div>
        </div>

        <div class="relative">
            <BaseOverlay
                :is-open="isLoading"
                class="md:rounded-lg"
            >
                <div class="flex h-full items-center justify-center">
                    <BaseSpinner class="h-24 w-24" />
                </div>
            </BaseOverlay>
            <div>
                <slot v-if="!isEmpty" />

                <div v-else>
                    <BaseEmptyState :has-action="false">
                        <template #icon> <ExclamationCircleIcon /> </template>

                        {{ t('general.noData') }}
                    </BaseEmptyState>
                </div>

                <BasePagination
                    v-if="meta"
                    class="mt-6"
                    :meta="meta"
                    :has-meta-info="true"
                    @update:page="(newPage) => updatePage(newPage)"
                    @update:page-size="(newPageSize) => updatePage(1, newPageSize)"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Attribute, IndexValidationError, PageInformation, PaginationMeta } from '@/types';
import BasePagination from './BasePagination.vue';
import BaseOverlay from './BaseOverlay.vue';
import BaseSpinner from './BaseSpinner.vue';
import BaseFieldInput from './BaseFieldInput.vue';
import IconMagnifyingGlass from '@/modules/your/components/icons/IconMagnifyingGlass.vue';
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline';

const { t } = useI18n();

interface UpdatePageEventArgs extends PageInformation {
    sortings: string[];
    search: string;
}

const emits = defineEmits<{
    (e: 'update:page', updatePageEventArgs: UpdatePageEventArgs): void;
    (e: 'update:search', search: string): void;
}>();

interface Props {
    attributes: Attribute[];
    isEmpty?: boolean;
    isLoading?: boolean;
    meta?: PaginationMeta;
    search?: string;
    validationError?: IndexValidationError | null;
}

const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
    meta: undefined,
    isEmpty: false,
    search: '',
    validationError: null,
});

const searchableAttributeLabels = computed(() => {
    const labels = props.attributes
        .map((attribute) => {
            if (!attribute.isSearchable) {
                return null;
            }
            return attribute.label;
        })
        .filter((value) => value !== null)
        .join(', ');
    return labels;
});

function updatePage(page: number, pageSize?: number) {
    if (props.meta) {
        emits('update:page', {
            page,
            pageSize: pageSize ?? props.meta.per_page,
            sortings: [],
            search: props.search,
        } as UpdatePageEventArgs);
    }
}

const onSearchClear = () => {
    emits('update:search', '');
    updatePage(1);
};
</script>
